import React from 'react';
import { Box, Typography, Tooltip, Zoom, Divider, Paper, IconButton } from '@mui/material';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import { useAuth } from '../context/useAuth';

const GeneralSettings = ({ user }) => {
    const { addNotification } = useNotification();
    const { countryName, timezone, branding } = useAuth();

    const handleCopyId = () => {
        navigator.clipboard.writeText(user?.tenantId || '');
        addNotification("Tenant ID copied to clipboard", "success");
    };

    const isActive = (user?.tenantStatus || 'active').toLowerCase() === 'active';

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
            {/* Header Section */}
            <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" sx={{ color: 'text.primary', mb: 1, letterSpacing: '-0.02em' }}>
                        General Settings
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Manage your organization's core identity and regional preferences.
                    </Typography>
                </Box>
                
                <Box 
                    sx={{ 
                        px: 1.75, 
                        py: 0.75, 
                        bgcolor: isActive ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
                        color: isActive ? 'success.main' : 'warning.main',
                        borderRadius: 'var(--border-radius)',
                        fontSize: '0.75rem',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: '1px solid',
                        borderColor: isActive ? 'var(--color-success-border)' : 'var(--color-warning-border)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
                    }}
                >
                    <Box 
                        sx={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            bgcolor: isActive ? 'success.main' : 'warning.main', 
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { opacity: 0.4 },
                                '50%': { opacity: 1 },
                                '100%': { opacity: 0.4 },
                            }
                        }} 
                    />
                    {isActive ? 'Active' : (user?.tenantStatus || 'Inactive')}
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4, alignItems: 'stretch' }}>
                {/* Identity Section */}
                <Paper 
                    variant="outlined"
                    sx={{ 
                        p: 4, 
                        bgcolor: 'background.paper', 
                        borderRadius: 'var(--border-radius)', 
                        borderColor: 'divider',
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.01)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box sx={{ 
                                p: 1, 
                                bgcolor: 'rgba(var(--primary-rgb), 0.08)', 
                                borderRadius: 'var(--border-radius)', 
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <DynamicIcon name="Building2" className="w-5 h-5" />
                            </Box>
                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.05rem', color: 'text.primary' }}>
                                Organization Identity
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                                    Display Name
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1.5,
                                    mt: 1, 
                                    px: 2, 
                                    py: 1.5, 
                                    bgcolor: 'action.hover', 
                                    borderRadius: 'var(--border-radius)', 
                                    border: '1px solid', 
                                    borderColor: 'divider' 
                                }}>
                                    <DynamicIcon name="Building" className="w-4 h-4 text-gray-400" />
                                    <Typography variant="body2" fontWeight="700" sx={{ color: 'text.primary' }}>
                                        {user?.tenantName || user?.tenant?.name || 'My Organization'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                                    Tenant Identifier
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'space-between',
                                    gap: 1.5, 
                                    mt: 1,
                                    px: 2, 
                                    py: 1,
                                    bgcolor: 'action.hover', 
                                    borderRadius: 'var(--border-radius)', 
                                    border: '1px solid', 
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <DynamicIcon name="Hash" className="w-4 h-4 text-gray-400" />
                                        <Typography sx={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.85rem', color: 'text.primary', fontWeight: 600 }}>
                                            {user?.tenantId}
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Copy Tenant ID" Arrow transition={Zoom}>
                                        <IconButton 
                                            onClick={handleCopyId}
                                            size="small"
                                            sx={{ 
                                                color: 'text.secondary',
                                                p: 0.5,
                                                '&:hover': { color: 'primary.main', bgcolor: 'rgba(var(--primary-rgb), 0.08)' }
                                            }}
                                        >
                                            <DynamicIcon name="Copy" className="w-4 h-4" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Security notice box */}
                    <Box sx={{ mt: 4, p: 2, bgcolor: 'action.hover', borderRadius: 'var(--border-radius)', border: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <DynamicIcon name="ShieldAlert" className="w-4 h-4 text-primary-main" style={{ marginTop: '2px', color: 'var(--primary-color)' }} />
                        <Box>
                            <Typography variant="caption" fontWeight="700" sx={{ color: 'text.primary', display: 'block', mb: 0.5 }}>
                                Security & Isolation
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                                Your Tenant Identifier is unique. All platform data, flows, and dashboards are strictly isolated within this tenant boundary. Do not share this identifier.
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* Regional Settings Section */}
                <Paper 
                    variant="outlined"
                    sx={{ 
                        p: 4, 
                        bgcolor: 'background.paper', 
                        borderRadius: 'var(--border-radius)', 
                        borderColor: 'divider',
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.01)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%'
                    }}
                >
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box sx={{ 
                                p: 1, 
                                bgcolor: 'rgba(var(--secondary-rgb), 0.08)', 
                                borderRadius: 'var(--border-radius)', 
                                color: 'secondary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <DynamicIcon name="Globe2" className="w-5 h-5" />
                            </Box>
                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.05rem', color: 'text.primary' }}>
                                Regional Settings
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                            {/* Region */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    bgcolor: 'action.hover', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: '1px solid', 
                                    borderColor: 'divider',
                                    color: 'text.secondary'
                                }}>
                                    <DynamicIcon name="MapPin" className="w-4 h-4" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                        Region
                                    </Typography>
                                    <Typography variant="body2" fontWeight="700" sx={{ color: 'text.primary' }}>
                                        {countryName || 'US-East (Virginia)'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Timezone */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    bgcolor: 'action.hover', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: '1px solid', 
                                    borderColor: 'divider',
                                    color: 'text.secondary'
                                }}>
                                    <DynamicIcon name="Clock" className="w-4 h-4" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                        Timezone
                                    </Typography>
                                    <Typography variant="body2" fontWeight="700" sx={{ color: 'primary.main' }}>
                                        {timezone || 'UTC'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Date Format */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    bgcolor: 'action.hover', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: '1px solid', 
                                    borderColor: 'divider',
                                    color: 'text.secondary'
                                }}>
                                    <DynamicIcon name="Calendar" className="w-4 h-4" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                        Date Format
                                    </Typography>
                                    <Typography variant="body2" fontWeight="700" sx={{ color: 'text.primary' }}>
                                        {branding?.date_format || 'YYYY-MM-DD'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Time Format */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    bgcolor: 'action.hover', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: '1px solid', 
                                    borderColor: 'divider',
                                    color: 'text.secondary'
                                }}>
                                    <DynamicIcon name="Clock4" className="w-4 h-4" />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                        Time Format
                                    </Typography>
                                    <Typography variant="body2" fontWeight="700" sx={{ color: 'text.primary' }}>
                                        {branding?.time_format || 'HH:mm:ss'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Divider sx={{ borderStyle: 'dashed', my: 3 }} />

                    {/* Service Plan */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                                width: 36, 
                                height: 36, 
                                borderRadius: '50%', 
                                bgcolor: 'rgba(var(--primary-rgb), 0.08)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                border: '1px solid', 
                                borderColor: 'rgba(var(--primary-rgb), 0.15)',
                                color: 'primary.main'
                            }}>
                                <DynamicIcon name="Crown" className="w-4 h-4" />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                    Service Plan
                                </Typography>
                                <Typography variant="body1" fontWeight="800" sx={{ color: 'primary.main', fontSize: '1.05rem' }}>
                                    {user?.planName || 'Pro'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.75, 
                                px: 1.5, 
                                py: 0.5, 
                                bgcolor: 'var(--color-success-bg)', 
                                borderRadius: 'var(--border-radius)', 
                                color: 'success.main',
                                border: '1px solid',
                                borderColor: 'var(--color-success-border)'
                            }}
                        >
                            <DynamicIcon name="ShieldCheck" className="w-4 h-4" />
                            <Typography variant="caption" fontWeight="800" sx={{ letterSpacing: '0.05em' }}>ACTIVE</Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default GeneralSettings;
